/**
 * components/fiscal/fiscalUi.js
 * ----------------------------------------------------------------------------
 * The shared shell for the Tax menu's pages (Fiscal documents, Vendor tax
 * settings, EFRIS health): one page frame, one card header, one status pill,
 * one way to write a date and an amount.
 *
 * WHY. These three screens will be walked through together in front of URA.
 * If each carried its own paddings, pill colours and date format the walk
 * would read as three tools stitched together, and a reviewer would wonder
 * which one to trust. Everything visual that is not a table or a dialog lives
 * here; tables are `.lk-table` in a `.lk-table-frame` and dialogs are
 * components/ui, per the dashboard's house kit.
 */
import React from "react";

/**
 * The Tax pages' type, on the console scale (the --lk-text-* tokens in
 * assets/css/lusuku-overrides.css), in ONE family, the dashboard's own.
 *
 * WHY (23 Oct 2026). Izi asked for uniform fonts across these pages. They had
 * drifted: card titles in 15px next to 22px light headings, body text at 13px
 * beside 14px table rows, two different "big number" sizes (18px and 20px, and
 * a 34px verdict), FDNs and TINs in a monospace face the rest of the page never
 * uses, and buttons at 11.52px. Every size on a Tax page now comes from here,
 * so a page cannot pick its own:
 *
 *   title      15px bold   a card or panel title
 *   body       13px        body text, table cells, labels, inputs
 *   secondary  12px        subtitles, notes, hints, fix text
 *   caption    11px        column headers, meta lines, pills
 *   figure     20px bold   the count that is the point of a panel
 *   code                    fiscal numbers, TINs, codes: the same family as the
 *                           text around them, with digits that line up
 */
const SIZE = {
  xs: "var(--lk-text-xs, 11px)",
  sm: "var(--lk-text-sm, 12px)",
  base: "var(--lk-text, 13px)",
  md: "var(--lk-text-md, 15px)",
  xl: "var(--lk-text-xl, 20px)",
};

export const TYPE = Object.freeze({
  title: { fontSize: SIZE.md, fontWeight: 700, lineHeight: 1.3 },
  body: { fontSize: SIZE.base },
  secondary: { fontSize: SIZE.sm },
  caption: { fontSize: SIZE.xs },
  figure: { fontSize: SIZE.xl, fontWeight: 700, lineHeight: 1.15 },
  code: { fontFamily: "inherit", fontVariantNumeric: "tabular-nums", letterSpacing: "0.02em", whiteSpace: "nowrap" },
});

export const PAGE = {
  page: { padding: "0 0 40px", background: "#f8fafc", minHeight: "100vh" },
  /* Margin and padding come from the --lk-page-card-* variables in
     lusuku-overrides.css, which shrink on a phone so a table keeps the width
     (1 Nov 2026). */
  card: {
    margin: "var(--lk-page-card-my, 24px) var(--lk-page-card-mx, 28px)",
    background: "#fff",
    borderRadius: 14,
    boxShadow: "0 1px 3px rgba(15,23,42,0.06), 0 6px 20px rgba(15,23,42,0.05)",
    overflow: "hidden",
  },
  cardHeader: {
    padding: "18px var(--lk-page-card-px, 24px)",
    borderBottom: "1px solid #eef2f6",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
    flexWrap: "wrap",
  },
  cardTitle: { ...TYPE.title, color: "#0f172a", margin: 0, display: "flex", alignItems: "center", gap: 10 },
  cardSubtitle: { ...TYPE.secondary, color: "#64748b", margin: "3px 0 0", maxWidth: 720, lineHeight: 1.5 },
  cardBody: { padding: "18px var(--lk-page-card-px, 24px) 22px" },
  toolbar: { display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-end", marginBottom: 14 },
  count: { ...TYPE.caption, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 10px" },
  meta: { ...TYPE.caption, display: "block", color: "#64748b", marginTop: 2 },
  strong: { fontWeight: 600, color: "#0f172a" },
  /* Kept under its old name so every caller follows the change: codes and
     fiscal numbers are no longer set in a monospace face. */
  mono: TYPE.code,
  muted: { color: "#8595a8" },
  empty: { ...TYPE.body, textAlign: "center", padding: "32px 16px", color: "#64748b" },
  sectionTitle: { ...TYPE.caption, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 8px" },
  detailGrid: { ...TYPE.body, display: "grid", gridTemplateColumns: "180px 1fr", gap: "6px 16px", alignItems: "start" },
  detailLabel: { ...TYPE.secondary, color: "#64748b", fontWeight: 600, paddingTop: 1 },
  detailValue: { color: "#1e293b", overflowWrap: "anywhere" },
  /* A secondary line of prose (a note, a fix, a hint) under a heading or row. */
  note: { ...TYPE.secondary, color: "#64748b", lineHeight: 1.5 },
  /* The count a panel exists to show, and its label. */
  figure: { ...TYPE.figure, color: "#0f172a" },
  figureLabel: { ...TYPE.caption, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" },
};

/** Pill tones. Named by meaning, not by colour, so a call site says what it means.
 *  warn is solid Lusuku yellow with ink text (24 Oct 2026; it was brown on
 *  cream). See "The Lusuku palette" in assets/css/lusuku-overrides.css. */
const PILL_TONES = {
  good: { bg: "#e6faf7", fg: "#047857" },
  warn: { bg: "#F2C811", fg: "#0F172A" },
  bad: { bg: "#fdf0f0", fg: "#b42318" },
  info: { bg: "#eaf2ff", fg: "#1d4ed8" },
  purple: { bg: "#f1edff", fg: "#6d28d9" },
  neutral: { bg: "#eef2f7", fg: "#51637a" },
};

export const Pill = ({ tone = "neutral", children, title, className }) => {
  const t = PILL_TONES[tone] || PILL_TONES.neutral;
  return (
    <span
      className={["lk-pill", className].filter(Boolean).join(" ")}
      title={title}
      style={{ ...TYPE.caption, background: t.bg, color: t.fg, fontWeight: 700, letterSpacing: "0.04em" }}
    >
      {children}
    </span>
  );
};

/** Document status, in the words ops uses, with its tone. Unknown statuses pass through. */
export const DOCUMENT_STATUS = Object.freeze({
  PENDING: { label: "Pending", tone: "neutral" },
  SUBMITTING: { label: "Submitting", tone: "info" },
  SUBMITTED: { label: "Submitted", tone: "good" },
  REJECTED: { label: "Rejected", tone: "bad" },
  AWAITING_APPROVAL: { label: "Awaiting URA approval", tone: "warn" },
  /* A credit note URA cancelled, or an application URA voided. The record and
     any FDN stay; the document no longer stands. */
  WITHDRAWN: { label: "Withdrawn", tone: "neutral" },
});

export const StatusPill = ({ status }) => {
  const meta = DOCUMENT_STATUS[status] || { label: String(status || "Unknown"), tone: "neutral" };
  return <Pill tone={meta.tone}>{meta.label}</Pill>;
};

export const KIND_LABEL = Object.freeze({ INVOICE: "Invoice", CREDIT_NOTE: "Credit note" });

export const fmtVendorType = (type) => {
  if (!type) return "";
  return String(type).split("_").map((w) => w.charAt(0) + w.slice(1).toLowerCase()).join(" ");
};

export const fmtDateTime = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("en-UG", { dateStyle: "medium", timeStyle: "short" });
};

export const fmtClock = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleTimeString("en-UG", { timeStyle: "medium" });
};

export const fmtMoney = (amount, currency = "UGX") => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return null;
  return `${currency || "UGX"} ${n.toLocaleString("en-UG", { maximumFractionDigits: 2 })}`;
};

/** "vendorType" to "Vendor type", "uraDetail" to "URA detail". For keys we did not name ourselves. */
export const humaniseKey = (key) => {
  const spaced = String(key || "")
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim();
  const words = spaced.split(/\s+/).map((w) => (/^(ura|efris|fdn|tin|dsn|api|url|id|vat|uat|tls|dns|s2s)$/i.test(w) ? w.toUpperCase() : w.toLowerCase()));
  if (!words.length) return "";
  words[0] = words[0].charAt(0).toUpperCase() + words[0].slice(1);
  return words.join(" ");
};

export const errorText = (err, fallback) =>
  err?.response?.data?.message || err?.message || fallback;

/** The standing "you may look but not touch" notice, same words on every page. */
export const DeniedNotice = ({ what }) => (
  <div style={PAGE.page}>
    <div style={PAGE.card}>
      <div style={PAGE.cardBody}>
        <div className="lk-note lk-note--warning">
          <span>
            <strong>Tax staff only.</strong> {what} This page is restricted to Lusuku staff who hold the tax
            permission; ask a super admin if you need it.
          </span>
        </div>
      </div>
    </div>
  </div>
);
